import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    Button,
    Container,
    Row,
    Col,
    Spinner,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faTimesCircle,
    faClock,
    faUser,
    faHome,
    faCar,
    faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import Logo from '../navbar/Logo';

const PublicVisitorValidation = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [visitor, setVisitor] = useState(null);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const apiUrl = `${import.meta.env.VITE_API_URL}/api`;
    const brandColor = "#950ec3"; // Homelan Purple

    useEffect(() => {
        fetchVisitorInfo();
    }, [token]);

    const fetchVisitorInfo = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${apiUrl}/public/visitor/${token}`, {
                headers: { 'Accept': 'application/json' }
            });
            const data = await response.json();

            if (!response.ok) throw new Error(data.message || "Error al cargar la información.");

            setVisitor(data.visitor);
            setStatus(data.status);
            setError(null);
        } catch (err) {
            setError(err.message || "Error al cargar la información del pase.");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmEntry = async () => {
        try {
            setSubmitting(true);
            const response = await fetch(`${apiUrl}/public/visitor/${token}/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            const data = await response.json();

            if (!response.ok) throw new Error(data.message || "Error al confirmar la entrada.");

            setSuccess(true);
            fetchVisitorInfo();
        } catch (err) {
            alert(err.message || "Error al confirmar la entrada.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
                <Spinner style={{ color: brandColor }} />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5 text-center px-4 d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="mb-4">
                    <FontAwesomeIcon icon={faTimesCircle} size="5x" className="text-danger" />
                </div>
                <h2 className="font-weight-bold mb-3">Pase Inválido</h2>
                <p className="lead text-muted mb-4">{error}</p>
                <Button
                    style={{ backgroundColor: brandColor, border: 'none' }}
                    size="lg"
                    className="rounded-pill px-5 shadow"
                    onClick={() => window.location.reload()}
                >
                    Reintentar
                </Button>
            </Container>
        );
    }

    const { is_valid, is_used, is_expired, is_future } = status;

    let statusTheme = {
        color: brandColor,
        icon: faClock,
        label: "Pase Válido",
    };

    if (success) {
        statusTheme = { color: "#00d27a", icon: faCheckCircle, label: "Entrada Confirmada" };
    } else if (!is_valid) {
        statusTheme = { color: "#e63757", icon: faExclamationTriangle, label: "Pase No Válido" };
    }

    return (
        <div className="bg-light" style={{ minHeight: '100vh', paddingBottom: '2rem' }}>
            <div className="text-center py-4 mb-2">
                <Logo width={160} />
            </div>

            <Container style={{ maxWidth: "450px" }}>
                <Card className="shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                    <div style={{ height: '8px', backgroundColor: statusTheme.color }} />

                    <CardBody className="p-4 bg-white">
                        <div className="text-center mb-4">
                            <div className="mb-3 d-inline-flex align-items-center justify-content-center rounded-circle border" style={{ width: '80px', height: '80px', backgroundColor: '#fcfaff' }}>
                                <FontAwesomeIcon icon={statusTheme.icon} size="2x" style={{ color: statusTheme.color }} />
                            </div>
                            <h2 className="font-weight-bold mb-1 text-dark" style={{ fontSize: '1.5rem' }}>{statusTheme.label}</h2>

                            {!success && !is_valid && (
                                <div className="badge badge-soft-danger px-3 py-2 mt-2" style={{ fontSize: '0.8rem' }}>
                                    {(is_used || status.entries_count >= status.limit_entries) ? "Límite de entradas alcanzado" : is_expired ? "Expirado" : is_future ? "Fecha futura" : "Sin permisos"}
                                </div>
                            )}
                            {is_valid && !success && (
                                <div className="mt-2">
                                    <p className="text-muted mb-0 small sans-serif">Validación de acceso para visitantes</p>
                                    <div className="badge badge-soft-info px-2 py-1 mt-1">
                                        Entradas disponibles: {status.limit_entries - status.entries_count} de {status.limit_entries}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white p-3 rounded-lg mb-4 border border-light shadow-sm" style={{ borderLeft: `4px solid ${brandColor}` }}>
                            <label className="text-uppercase ls-1 font-weight-bold text-500 fs--2 mb-2 d-block">Visitante Autorizado</label>
                            <div className="d-flex align-items-center">
                                <div className="avatar avatar-3xl mr-3">
                                    <div className="avatar-name rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center">
                                        <FontAwesomeIcon icon={faUser} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-weight-extrabold mb-0 text-dark">
                                        {visitor.firstname} {visitor.lastname}
                                    </h4>
                                    <p className="text-600 mb-0 small"><b>DNI:</b> {visitor.dni}</p>
                                </div>
                            </div>
                        </div>

                        <Row className="mb-4">
                            <Col xs={12} className="mb-3">
                                <div className="p-3 border rounded-lg bg-light h-100">
                                    <label className="text-uppercase ls-1 font-weight-bold text-500 fs--2 mb-2 d-block">Destino</label>
                                    <div className="d-flex align-items-start font-weight-bold">
                                        <FontAwesomeIcon icon={faHome} className="text-primary mr-2 mt-1" />
                                        <div>
                                            <div className="text-dark fs-0">{visitor.property}</div>
                                            <div className="text-600 small">{visitor.condominium}</div>
                                            {visitor.host_name && (
                                                <div className="text-primary small mt-1">
                                                    Residente: {visitor.host_name}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <div className="p-3 border rounded-lg bg-light h-100">
                                    <label className="text-uppercase ls-1 font-weight-bold text-500 fs--2 mb-2 d-block">Vehículo</label>
                                    <div className="d-flex align-items-center font-weight-bold">
                                        <FontAwesomeIcon icon={faCar} className="text-primary mr-2" />
                                        <span className="text-dark fs-0">
                                            {visitor.plate_number || "A pie / Sin vehículo"}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <div className="mb-4">
                            <label className="text-uppercase ls-1 font-weight-bold text-500 fs--2 mb-2 d-block text-center">Validez del Pase</label>
                            <div className="d-flex align-items-center justify-content-center p-3 border rounded-lg bg-white shadow-none">
                                <div className="text-center px-3 border-right flex-fill">
                                    <div className="text-500 small ls-1">ENTRADA</div>
                                    <div className="font-weight-bold text-dark">{moment(visitor.start_at).format("DD/MM/YYYY")}</div>
                                    <div className="text-primary font-weight-bold fs--1">{moment(visitor.start_at).format("hh:mm A")}</div>
                                </div>
                                <div className="text-center px-3 flex-fill">
                                    <div className="text-500 small ls-1">SALIDA</div>
                                    <div className="font-weight-bold text-dark">{moment(visitor.end_at).format("DD/MM/YYYY")}</div>
                                    <div className="text-primary font-weight-bold fs--1">{moment(visitor.end_at).format("hh:mm A")}</div>
                                </div>
                            </div>
                        </div>

                        {is_valid && !success && (
                            <Button
                                block
                                size="lg"
                                style={{ backgroundColor: brandColor, borderColor: brandColor }}
                                className="py-3 font-weight-bold mt-4 shadow-sm rounded-lg"
                                onClick={handleConfirmEntry}
                                disabled={submitting}
                            >
                                {submitting ? <Spinner size="sm" /> : "Registrar Entrada"}
                            </Button>
                        )}

                        {success && (
                            <div className="text-center p-4 bg-soft-success rounded-lg border border-success mt-4">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-success mb-2" size="2x" />
                                <h4 className="text-success font-weight-bold mb-0">Entrada Registrada</h4>
                                <p className="text-success small mb-0 mt-1">Acceso permitido correctamente</p>
                            </div>
                        )}
                    </CardBody>
                </Card>

                <div className="text-center mt-5 mb-4">
                    <p className="text-400 fs--2 mb-0 font-weight-bold">
                        &copy; HOME LAN APP - SEGURIDAD INTELIGENTE
                    </p>
                </div>
            </Container>
        </div>
    );
};

export default PublicVisitorValidation;
